import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useBilling } from "@/hooks/useBilling";
import { BillingNotConfiguredError, openCheckout } from "@/lib/paddle";
import {
  PLANS,
  TIER_DESCRIPTIONS,
  TIER_ORDER,
  formatPlanPrice,
  getPriceId,
  intervalLabel,
  isPaddleConfigured,
  plansForTier,
  tierLabel,
  type BillingInterval,
  type PlanDefinition,
  type Tier,
} from "@/lib/billingConfig";

const TIER_FEATURES: Record<Tier, string[]> = {
  family: [
    "Unlimited scenarios and drafts",
    "Unlimited Family Banking Commitment Agreements™",
    "Full commitment schedule and print-ready reports",
    "Actual payment tracking and the Family Loan Timeline™",
    "Lump sums, stress tests and multi-policy planning",
  ],
  advisor_core: [
    "Everything in Family",
    "Advisor code linking with explicit client consent",
    "Read-only review of linked client scenarios",
    "Client-ready report exports",
  ],
  advisor_pro: [
    "Everything in Advisor Core",
    "Higher linked-client capacity",
    "Priority support",
  ],
};

/**
 * Family is annual-only by product decision: the commitment model is a
 * year-at-a-time discipline, and monthly billing works against that.
 */
function pickPlan(tier: Tier, interval: BillingInterval): PlanDefinition | null {
  const candidates = plansForTier(tier).filter((p) => !p.cohortRestricted);
  return candidates.find((p) => p.interval === interval) ?? candidates[0] ?? null;
}

export default function Pricing() {
  const { user } = useAuth();
  const { billing } = useBilling();
  const nav = useNavigate();
  const [interval, setInterval] = useState<BillingInterval>("year");
  const [busy, setBusy] = useState<string | null>(null);

  const configured = isPaddleConfigured();
  const currentTier = billing?.entitlement?.tier ?? null;

  const cards = useMemo(
    () => TIER_ORDER.map((tier) => ({ tier, plan: pickPlan(tier, interval) })),
    [interval],
  );

  async function handleSelect(plan: PlanDefinition | null) {
    if (!plan) return;
    if (!user) {
      nav("/auth?next=/pricing");
      return;
    }
    setBusy(plan.key);
    try {
      await openCheckout({ planKey: plan.key, userId: user.id, email: user.email });
    } catch (e) {
      if (e instanceof BillingNotConfiguredError) {
        toast.error("Checkout isn't available yet", {
          description: "Billing is still being set up for this environment.",
        });
      } else {
        toast.error("We couldn't open checkout. Please try again.");
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Pricing — Family Loan Planner™</title>
        <meta
          name="description"
          content="Plans for families and advisors running a disciplined family banking system. Annual Family plan, plus Advisor Core and Advisor Pro."
        />
        <link rel="canonical" href="https://app.familyloanplanner.com/pricing" />
      </Helmet>

      <main className="container mx-auto px-4 py-10 max-w-6xl">
        <header className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Choose your plan</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
            Every plan keeps your loan history, schedules and reports. Plans differ in how many
            commitments you can run and whether you work with client families.
          </p>
        </header>

        {!configured && (
          <Card className="p-4 mb-8 border-amber-500/40 bg-amber-500/5">
            <p className="text-sm font-medium">Checkout is not connected yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              This environment has no payment provider configured, so the buttons below are
              disabled. Everything already in your account continues to work normally.
            </p>
          </Card>
        )}

        <div className="flex items-center justify-center gap-1 mb-8 rounded-lg border p-1 w-fit mx-auto">
          {(["month", "year"] as BillingInterval[]).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setInterval(opt)}
              aria-pressed={interval === opt}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                interval === opt
                  ? "bg-primary text-primary-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt === "month" ? "Monthly" : "Annual"}
            </button>
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {cards.map(({ tier, plan }) => {
            const unavailable = !plan || !configured || getPriceId(plan.key) === null;
            const isCurrent = currentTier === tier && !!billing?.canUsePaidFeatures;
            const annualOnly = tier === "family" && interval === "month";

            return (
              <Card key={tier} className="p-6 flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-lg font-semibold">{tierLabel(tier)}</h2>
                  {isCurrent && <Badge>Current plan</Badge>}
                </div>
                <p className="text-sm text-muted-foreground mt-2 min-h-[3.5rem]">
                  {TIER_DESCRIPTIONS[tier]}
                </p>

                <div className="mt-4">
                  {plan ? (
                    <>
                      <span className="text-3xl font-bold tabular-nums">{formatPlanPrice(plan)}</span>
                      <span className="text-sm text-muted-foreground ml-1">
                        {intervalLabel(plan.interval)}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">Not available</span>
                  )}
                  {annualOnly && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Family is billed annually — a year at a time matches the commitment cycle.
                    </p>
                  )}
                </div>

                <ul className="mt-5 space-y-2 text-sm flex-1">
                  {TIER_FEATURES[tier].map((f) => (
                    <li key={f} className="flex gap-2">
                      <span aria-hidden className="text-primary mt-0.5">✓</span>
                      <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="mt-6 w-full"
                  disabled={unavailable || busy !== null || isCurrent}
                  onClick={() => handleSelect(plan)}
                >
                  {isCurrent
                    ? "Your current plan"
                    : busy === plan?.key
                      ? "Opening checkout…"
                      : unavailable
                        ? "Coming soon"
                        : `Choose ${tierLabel(tier)}`}
                </Button>
              </Card>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground text-center mt-8 max-w-2xl mx-auto">
          Founding and second-cohort Family pricing is applied by invitation and does not appear
          here. Prices are in USD. You can review or change your plan any time on the{" "}
          <Link to="/billing" className="underline">billing page</Link>.
        </p>
        <p className="sr-only">
          Family standard {formatPlanPrice(PLANS.family_standard_yearly)} per year.
        </p>
      </main>
    </div>
  );
}
